import React from 'react';
import { Box, Typography, Link, Container, Divider, useTheme, Stack } from '@mui/material';

/**
 * Footer component with copyright information, organization attribution, and policy links
 * @returns {JSX.Element} Footer component
 */
const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        px: 2,
        mt: 'auto',
        backgroundColor: theme.palette.grey[100],
      }}
    >
      <Divider sx={{ mb: 2 }} />
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'center', sm: 'flex-start' },
          textAlign: { xs: 'center', sm: 'left' },
          mb: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            {currentYear} Glossa.link. All rights reserved.
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: { xs: 1, sm: 0 } }}>
            Owned and operated by{' '}
            <Link 
              href="https://bridgebc.org" 
              target="_blank" 
              rel="noopener noreferrer"
              color="primary"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              The Bridge Church at Bear Creek
            </Link>
          </Typography>
        </Box>
        
        {/* Policy Links */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'center',
          mt: 1
        }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1, sm: 2 }}
            divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
            sx={{ textAlign: 'center' }}
          >
            <Link 
              href="/policies/terms-and-conditions.pdf" 
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
              sx={{ fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Terms & Conditions
            </Link>
            <Link 
              href="/policies/disclaimer.pdf" 
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
              sx={{ fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Disclaimer
            </Link>
            <Link 
              href="/policies/privacy-notice.pdf" 
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
              sx={{ fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Privacy Notice
            </Link>
            <Link 
              href="/policies/cookie-policy.pdf" 
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
              sx={{ fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Cookie Policy
            </Link>
            <Link 
              href="/policies/acceptable-use-policy.pdf" 
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
              sx={{ fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Acceptable Use Policy
            </Link>
            <Link 
              href="mailto:support@glossa.link"
              color="text.secondary"
              sx={{ fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Contact Support
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
